import React, { memo, useCallback, useLayoutEffect, useRef, useState, } from 'react';
import { ArrowUp, Check, ChevronDown, CornerDownRight, Lock, Square, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { useChatStore } from 'store/chat.store';
import { useChatTiers } from 'modules/llm';
import { FEATURES, useOpenUpgradeForFeature } from 'modules/entitlements';

interface ChatSubmitProps {
    isSubmitting: boolean;
    handleSubmit: (text: string) => void;
    onStop?: () => void;
    className?: string;
    /** Keys the unsent draft in localStorage: one draft per notebook. */
    notebookId?: string;
    /** Blocks typing and sending (e.g. while the document is still processing). */
    disabled?: boolean;
}
// The composer is two lines tall at rest (14px × 1.5 line height) and grows with the draft.
const MIN_TEXTAREA_HEIGHT = 42;
const MAX_TEXTAREA_HEIGHT = 200;
const ChatSubmit: React.FC<ChatSubmitProps> = ({ isSubmitting, handleSubmit: submit, onStop, className, notebookId, disabled = false, }) => {
    const { t } = useTranslation();
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [isModelOpen, setIsModelOpen] = useState(false);
    const modelDropdownRef = useRef<HTMLDivElement>(null);
    const [currentQuery, setCurrentQuery] = useState(() => (notebookId ? localStorage.getItem(`chat-message-${notebookId}`) ?? '' : ''));
    const { highlightedText, setHighlightedText, selectedTier, setSelectedTier, } = useChatStore();
    const { tiers, unlockedTiers, isLoading: tiersLoading } = useChatTiers();
    const openUpgrade = useOpenUpgradeForFeature();
    // Resolve the active tier: the persisted choice when the plan still grants
    // it, otherwise the cheapest rung it does. Null while the ladder loads or
    // when the org has no chat access at all.
    const activeTier = unlockedTiers.find((tier) => tier.key === selectedTier) ?? unlockedTiers[0] ?? null;
    const currentTierLabel = activeTier?.label ?? (tiersLoading ? t("loading") : t("model"));
    const placeholder = disabled ? t("chat_unlocks_when_processing_completes") : t("ask_anything_about_this_document");
    const adjustTextareaHeight = useCallback(() => {
        const textarea = textareaRef.current;
        if (!textarea)
            return;
        textarea.style.height = 'auto';
        const newHeight = Math.max(Math.min(textarea.scrollHeight, MAX_TEXTAREA_HEIGHT), MIN_TEXTAREA_HEIGHT);
        textarea.style.height = `${newHeight}px`;
        textarea.style.overflowY = textarea.scrollHeight > MAX_TEXTAREA_HEIGHT ? 'auto' : 'hidden';
    }, []);
    useLayoutEffect(() => {
        adjustTextareaHeight();
    }, [currentQuery, adjustTextareaHeight]);
    const resetTextareaHeight = useCallback(() => {
        const textarea = textareaRef.current;
        if (!textarea)
            return;
        textarea.style.height = `${MIN_TEXTAREA_HEIGHT}px`;
        textarea.style.overflowY = 'hidden';
    }, []);
    // Allow stopping via Escape key while submitting
    React.useEffect(() => {
        if (!isSubmitting || !onStop)
            return;
        const handleGlobalKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                onStop();
            }
        };
        window.addEventListener('keydown', handleGlobalKeyDown);
        return () => window.removeEventListener('keydown', handleGlobalKeyDown);
    }, [isSubmitting, onStop]);

    // Close model dropdown when clicking outside
    React.useEffect(() => {
        if (!isModelOpen)
            return;
        const handleClickOutside = (e: MouseEvent) => {
            if (modelDropdownRef.current && !modelDropdownRef.current.contains(e.target as Node)) {
                setIsModelOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isModelOpen]);
    const submitMessage = useCallback(() => {
        if (!currentQuery.trim() || isSubmitting || disabled)
            return;
        submit(currentQuery);
        setCurrentQuery('');
        if (notebookId)
            localStorage.removeItem(`chat-message-${notebookId}`);
        resetTextareaHeight();
    }, [currentQuery, isSubmitting, disabled, submit, notebookId, resetTextareaHeight]);
    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault();
            submitMessage();
        }
    };
    const hasContent = !!currentQuery.trim() || !!highlightedText;
    return (<div className={className}>
      <div ref={containerRef} className={cn('rounded-lg border border-border bg-card transition-colors focus-within:border-foreground/30', disabled ? 'cursor-not-allowed bg-muted/50' : 'cursor-text')} onClick={() => textareaRef.current?.focus()}>
        {/* Quote context banner */}
        {highlightedText && (<div className="flex items-start gap-2 px-3 pt-2.5">
            <div className="flex min-w-0 flex-1 items-start gap-2 rounded-md bg-muted px-2.5 py-2">
              <CornerDownRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground"/>
              <span className="line-clamp-2 flex-1 text-xs text-foreground/80">
                {highlightedText}
              </span>
            </div>
            <button type="button" aria-label={t("close")} onClick={(e) => { e.stopPropagation(); setHighlightedText(null); }} className="mt-0.5 shrink-0 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              <X className="h-3.5 w-3.5"/>
            </button>
          </div>)}

        {/* The draft gets the full width; the answer tier and send/stop sit on a toolbar under it. */}
        <div className="px-3 pt-3">
          <textarea ref={textareaRef} value={currentQuery} onChange={(e) => {
            setCurrentQuery(e.target.value);
            if (notebookId)
                localStorage.setItem(`chat-message-${notebookId}`, e.target.value);
        }} onKeyDown={handleKeyDown} disabled={isSubmitting || disabled} rows={2} placeholder={placeholder} aria-label={placeholder} style={{ outline: 'none', boxShadow: 'none', resize: 'none' }} className="block min-h-[42px] w-full min-w-0 border-0 bg-transparent p-0 focus-visible:!outline-none text-sm leading-normal text-foreground placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"/>

        </div>

        <div className="flex items-center justify-between gap-2 px-2.5 pb-2.5 pt-1.5">
          {/* Answer tier */}
          <div className="relative shrink-0" ref={modelDropdownRef}>
            <button type="button" aria-expanded={isModelOpen} onClick={(e) => { e.stopPropagation(); setIsModelOpen((p) => !p); }} className={cn('relative inline-flex h-9 items-center gap-1.5 rounded-full border border-border bg-card px-3 text-[12.5px] font-medium text-foreground/80 transition-colors before:absolute before:-inset-1 hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring', isModelOpen && 'bg-muted text-foreground')}>
              <span className="max-w-[96px] truncate">{currentTierLabel}</span>
              <ChevronDown className={cn('h-3.5 w-3.5 transition-transform duration-150', isModelOpen && 'rotate-180')}/>
            </button>

            {isModelOpen && (<div className="absolute bottom-full left-0 z-50 mb-1.5 w-64 overflow-hidden rounded-lg border border-border bg-popover p-1 text-popover-foreground shadow-md">
                <p className="px-2 pb-1 pt-1.5 text-[12.5px] font-semibold text-muted-foreground">{t("response_quality")}</p>
                {tiers.length === 0 ? (
                  <p className="px-2 py-2 text-xs text-muted-foreground">
                    {tiersLoading ? t("loading") : t("no_models_available")}
                  </p>
                ) : (
                  // Locked rungs stay on the list: seeing what a better plan gets
                  // you is the pitch, and hiding it makes the ladder invisible.
                  tiers.map((tier) => {
                    const isActive = activeTier?.key === tier.key;
                    return (<button key={tier.key} type="button" title={tier.description || undefined} aria-current={isActive ? 'true' : undefined} onClick={(e) => {
                          e.stopPropagation();
                          setIsModelOpen(false);
                          if (tier.locked) { openUpgrade(FEATURES.AI_CHAT); return; }
                          setSelectedTier(tier.key);
                      }} className={cn('flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left transition-colors hover:bg-accent', isActive && 'bg-muted')}>
                        <span className="min-w-0 flex-1">
                          <span className={cn('block truncate text-sm text-foreground', isActive ? 'font-semibold' : 'font-medium')}>
                            {tier.label}
                          </span>
                          {tier.tagline && (<span className="block truncate text-[11.5px] text-muted-foreground">
                            {tier.tagline}
                          </span>)}
                        </span>
                        {tier.locked ? (<Badge className="shrink-0">
                            <Lock className="h-3 w-3"/>
                            {t("upgrade")}
                          </Badge>) : isActive ? (<Check className="h-4 w-4 shrink-0 text-foreground"/>) : null}
                      </button>);
                  })
                )}
              </div>)}
          </div>

          {/* Send (the screen's lime action) or, while an answer streams, the one stop button.
              Like the tier button, each keeps a 44px hit area (`before:-inset-1`) for thumbs. */}
          {isSubmitting ? (
            <Button
              type="button"
              variant="ink"
              size="icon"
              data-testid="chat-stop-button"
              onClick={(e) => {
                e.stopPropagation();
                onStop?.();
              }}
              title={t("stop_generating")}
              aria-label={t("stop_generating")}
              className="relative shrink-0 before:absolute before:-inset-1"
            >
              <Square className="h-2.5 w-2.5 fill-current" />
            </Button>
          ) : (
            <Button
              type="button"
              size="icon"
              data-testid="chat-submit-button"
              onClick={(e) => {
                e.stopPropagation();
                submitMessage();
              }}
              disabled={!hasContent || disabled}
              title={t("submit")}
              aria-label={t("submit")}
              className="relative shrink-0 before:absolute before:-inset-1 disabled:bg-muted disabled:text-muted-foreground disabled:opacity-100"
            >
              <ArrowUp className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      <div className="mt-2 flex items-start justify-between gap-3 text-xs text-muted-foreground">
        <span>{t("answers_grounded_strictly_in_the_uploaded_document")}</span>
        <span className="hidden shrink-0 select-none [@media(hover:hover)]:inline">{t("u21b5_send")}</span>
      </div>
    </div>);
};
export default memo(ChatSubmit);
